import { _ } from "@sablier/v2-mixins";
import { assign, createMachine } from "xstate";

type MachineContext<Payload> = {
  creating?: boolean;
  error: string | undefined;
  payload: Payload;
};

type MachineServices = {
  doCreate: { data: unknown };
  doCheck: { data: boolean };
};

type MachineEvents<Payload> =
  | {
      type: "CREATE";
      payload: Payload;
    }
  | { type: "RESET" }
  | { type: "FAIL" };

type Action<Payload> = ({
  context,
}: {
  context: MachineContext<Payload>;
}) => Promise<void>;

interface Props<Payload> {
  id: string;
  onCreate: Action<Payload>;
}

// create a re-usable state machine for things that need to be deployed/created in their absence (e.g. proxies).
function create<Payload>({ id, onCreate }: Props<Payload>) {
  return createMachine(
    {
      tsTypes: {} as import("./index.typegen").Typegen0,
      id: `instantiator-${id}`,
      initial: "idle",
      context: { creating: false, payload: {} as Payload, error: undefined },
      predictableActionArguments: true,
      schema: {
        context: {} as MachineContext<Payload>,
        events: {} as MachineEvents<Payload>,
        services: {} as MachineServices,
      },
      states: {
        idle: {
          entry: "doContextClean",
          on: {
            FAIL: {
              target: "failed",
            },
            CREATE: {
              target: "creating",
            },
          },
        },
        created: {
          on: {
            RESET: {
              target: "idle",
            },
            FAIL: {
              target: "failed",
            },
          },
        },
        creating: {
          entry: assign({
            creating: () => true,
          }),
          invoke: {
            id: "approve",
            src: "doCreate",
            onDone: {
              target: "created",
            },
            onError: {
              actions: "doError",
              target: "failed",
            },
          },
          on: {
            RESET: {
              target: "idle",
            },
          },
          exit: assign({ creating: () => false }),
        },
        failed: {
          on: {
            RESET: {
              target: "idle",
            },
            CREATE: {
              target: "creating",
            },
          },
        },
      },
    },
    {
      actions: {
        doContextClean: assign({
          error: (_context, _event) => undefined,
        }),
        doError: assign({
          error: (_context, event) => _.toString(_.get(event, "data.message")),
        }),
      },
      services: {
        doCreate: async (context, event) => {
          await onCreate({ context: { ...context, payload: event.payload } });
        },
      },
    },
  );
}

const instantiator = {
  create,
};

export { instantiator };
